"use client";

import React from "react";
import { ArrowRight, Compass, Hammer } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultationCta() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    once: true
  });
  const outdoorImage = {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_patio_renovation.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_patio_renovation.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/outdoor_living_patio_renovation.webp-wvc-sizes",
    alt: "Covered outdoor living patio renovation with tongue-and-groove cedar ceiling, stone fireplace, and comfortable lounge seating in Brookhaven"
  };
  return <section id="consultationcta" data-nav="light" ref={ref} style={{
    opacity: isVisible ? 1 : 0
  }} className="bg-background text-foreground py-16 sm:py-24 transition-opacity duration-700 ease-out">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Visual column: realistic outdoor living photo */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative overflow-hidden rounded-2xl border border-border shadow-lg bg-card">
              <img src={outdoorImage.src} data-wvc-srcset={outdoorImage.srcset} data-wvc-sizes={outdoorImage.sizes} alt={outdoorImage.alt} loading="lazy" decoding="async" className="w-full h-[320px] sm:h-[420px] lg:h-[480px] object-cover" />
            </div>
          </div>

          {/* Copy and Actions column */}
          <div className="lg:col-span-6 order-1 lg:order-2 flex flex-col justify-center">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15]">
              Ready to elevate your outdoor and interior living spaces?
            </h2>

            <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed">
              Schedule a focused consultation with our team to review your architectural goals, site conditions, and budget.
            </p>

            {/* Primary Action */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="px-8 py-6 text-base font-medium rounded-md shadow-sm">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an Estimate
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>
            </div>

            <Separator className="my-8" />

            {/* Related Services Row */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground/60">
                <Hammer className="size-3.5" />
                <span>Related Services</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium">
                <Link to="#" className="text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                  Deck Remodeling
                </Link>
                <span className="text-border" aria-hidden="true">|</span>
                <Link to="/HomeAdditionsPage" className="text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                  Home Additions
                </Link>
              </div>
            </div>

            {/* Secondary Guide and Informational Links */}
            <div className="mt-6 pt-6 border-t border-border/60">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground/60 mb-3">
                <Compass className="size-3.5" />
                <span>Planning Resources</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground/80">
                <Link to="#" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
                  Remodeling Cost Guide
                </Link>
                <span className="text-border" aria-hidden="true">&bull;</span>
                <Link to="#" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
                  Remodeling Planning Guide
                </Link>
                <span className="text-border" aria-hidden="true">&bull;</span>
                <Link to="#" className="hover:text-primary transition-colors underline-offset-4 hover:underline">
                  Frequently Asked Questions
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}